import { FC, MouseEvent, ReactNode, useState } from "react";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { alpha } from "@mui/material/styles";

/**
 * The row action menu (design L262-275 for a table row, L317-330 for a card — identical markup).
 *
 * A KEBAB opening a menu, not a strip of icon buttons. The two views deliberately share this
 * component: the design gives both the same control, and a table that acts one way while cards act
 * another is the kind of divergence that makes a redesign feel unfinished.
 *
 * Design metrics: 32px trigger, 8px radius, `--surface-2` on hover; menu 190px wide, 8px radius,
 * 4px padding; items 36px tall, 14px, 10px gap, 6px radius, with the destructive one in
 * `--error-dark` over an `--error-light` hover.
 */

export interface ConfigRowAction {
	label: string;
	icon: ReactNode;
	onClick: () => void;
	/** Renders the item in the error colour — the destructive choice. */
	destructive?: boolean;
}

export interface ConfigRowActionsProps {
	/** Names the record so the trigger is distinguishable to a screen reader. */
	recordName: string;
	actions: ConfigRowAction[];
}

const ConfigRowActions: FC<ConfigRowActionsProps> = ({
	recordName,
	actions,
}) => {
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
	const open = Boolean(anchorEl);

	// Both views put the trigger inside a clickable row/card, so every handler stops propagation —
	// otherwise opening the menu (or choosing Delete) would also open the editor underneath.
	const openMenu = (event: MouseEvent<HTMLElement>): void => {
		event.stopPropagation();
		setAnchorEl(event.currentTarget);
	};

	return (
		<Box sx={{ position: "relative" }} onClick={(e) => e.stopPropagation()}>
			<Tooltip title="Actions">
				<IconButton
					aria-label={`Actions for ${recordName}`}
					aria-haspopup="menu"
					aria-expanded={open}
					onClick={openMenu}
					sx={{
						width: "2rem",
						height: "2rem",
						borderRadius: "0.5rem",
						color: "text.secondary",
						"&:hover": { backgroundColor: (t) => t.palette.surfaceTwo.main },
					}}
				>
					<MoreVertIcon sx={{ fontSize: "1.25rem" }} />
				</IconButton>
			</Tooltip>
			<Menu
				anchorEl={anchorEl}
				open={open}
				onClose={() => setAnchorEl(null)}
				anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
				transformOrigin={{ vertical: "top", horizontal: "right" }}
				slotProps={{
					paper: {
						sx: {
							width: "11.875rem",
							borderRadius: "0.5rem",
							padding: "0.25rem",
						},
					},
				}}
			>
				{actions.map((action) => (
					<MenuItem
						key={action.label}
						onClick={(e) => {
							e.stopPropagation();
							setAnchorEl(null);
							action.onClick();
						}}
						sx={{
							height: "2.25rem",
							minHeight: "2.25rem",
							padding: "0 0.625rem",
							gap: 1.25,
							borderRadius: "0.375rem",
							fontSize: "0.875rem",
							color: action.destructive ? "error.dark" : "text.primary",
							"&:hover": {
								backgroundColor: (t) =>
									action.destructive
										? t.palette.error.light
										: alpha(t.palette.primary.main, 0.06),
							},
						}}
					>
						{action.icon}
						{action.label}
					</MenuItem>
				))}
			</Menu>
		</Box>
	);
};

export default ConfigRowActions;
